/**
 * Единое окно «Сообщения / Уведомления / Задачи» из шапки (УВ-12).
 * «Задачи» — «Ждёт меня» и поручения; «Сообщения» — недавние обсуждения у записей (УВ-01);
 * лента оповещений (УВ-04) — позже.
 */
import {
  DISCUSSION_ENTITY_TYPES,
  type DiscussionThreadPreview,
  INBOX_SOURCES,
  type InboxItem,
  type Person,
  type ProjectSummary,
  type TaskView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { date, dateTime } from '../lib/format';
import { projectsQuery } from '../lib/projects';
import { useMe } from '../lib/session';
import { inboxQuery } from '../pages/InboxPage';
import { Button } from '../ui/button';
import { Dialog, Sheet } from '../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Field, Input, Select, Textarea } from '../ui/form';
import { type HubTab, readHubTab, writeHubTab } from './hub-tab';

const TABS: { id: HubTab; label: string }[] = [
  { id: 'messages', label: 'Сообщения' },
  { id: 'alerts', label: 'Уведомления' },
  { id: 'tasks', label: 'Задачи' },
];

export function HubPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [tab, setTab] = useState<HubTab>(readHubTab);
  useEffect(() => {
    writeHubTab(tab);
  }, [tab]);

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Сообщения и задачи"
      description="С любой страницы: сообщения, оповещения и то, что ждёт вас"
    >
      {open ? (
        <div className="flex flex-col gap-4">
          <div className="inline-flex w-full rounded-md border border-line bg-surface p-0.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  'h-11 flex-1 rounded-[3px] px-2 text-[13px] font-medium md:h-9',
                  tab === t.id ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'messages' ? <MessagesTab onOpenChange={onOpenChange} /> : null}
          {tab === 'alerts' ? <AlertsSoon /> : null}
          {tab === 'tasks' ? <TasksTab onOpenChange={onOpenChange} /> : null}
        </div>
      ) : null}
    </Sheet>
  );
}

const mineDiscussionsQuery = {
  queryKey: ['discussions', 'mine'] as const,
  queryFn: () => api<DiscussionThreadPreview[]>('/discussions/mine'),
};

function MessagesTab({ onOpenChange }: { onOpenChange: (v: boolean) => void }) {
  const q = useQuery(mineDiscussionsQuery);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data!.length) {
    return (
      <EmptyState title="Пока нет обсуждений">
        Напишите сообщение в карточке заявки на закупку или замечания — оно появится здесь. Личные и групповые
        чаты, непрочитанное и оповещения — позже.
      </EmptyState>
    );
  }
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-ink">Мои недавние обсуждения</h3>
        <span className="text-[12px] text-ink-2">{q.data!.length}</span>
      </div>
      <ul className="divide-y divide-line rounded-md border border-line">
        {q.data!.map((t) => (
          <li key={`${t.entityType}:${t.entityId}`} className="flex flex-col gap-2 px-3 py-3">
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
              <Badge tone="neutral">{DISCUSSION_ENTITY_TYPES[t.entityType]}</Badge>
              <span className="num">{dateTime(t.lastAt)}</span>
              <span>{t.messageCount} сообщ.</span>
            </div>
            <Link
              to={t.path}
              onClick={() => onOpenChange(false)}
              className="inline-flex min-h-11 items-center text-[14px] font-medium [overflow-wrap:anywhere] text-ink hover:underline md:min-h-0"
            >
              {t.title}
            </Link>
            <p className="truncate text-[13px] text-ink-2">
              {t.lastAuthorName}: {t.lastBody}
            </p>
            <Link
              to={t.path}
              onClick={() => onOpenChange(false)}
              className="inline-flex h-11 w-fit items-center rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8"
            >
              Открыть
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AlertsSoon() {
  return (
    <EmptyState title="Лента уведомлений скоро">
      Оповещения в портале со звуком и кнопками «Открыть» и «Принять в работу» появятся здесь. Сейчас срочные
      дела — во вкладке «Задачи», утренняя сводка — на почту и в Telegram (Профиль → Уведомления).
    </EmptyState>
  );
}

function TasksTab({ onOpenChange }: { onOpenChange: (v: boolean) => void }) {
  const me = useMe();
  const staff = Boolean(me.tenant?.staff);
  const q = useQuery(inboxQuery);
  const items = (q.data ?? []).filter((i) => i.source !== 'tasks');
  const overdue = items.filter((i) => i.overdue).length;

  return (
    <div className="flex flex-col gap-5">
      <section>
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h3 className="text-[14px] font-semibold text-ink">Ждёт меня</h3>
          {q.data ? (
            <span className="text-[12px] text-ink-2">
              {items.length}
              {overdue ? ` · просрочено ${overdue}` : ''}
            </span>
          ) : null}
        </div>
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : items.length ? (
          <ul className="divide-y divide-line rounded-md border border-line">
            {items.map((i) => (
              <InboxRow key={i.id} item={i} onNavigate={() => onOpenChange(false)} />
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">Сейчас дел нет.</p>
        )}
      </section>
      {staff ? <HubTasks /> : null}
      <Link
        to="/inbox"
        onClick={() => onOpenChange(false)}
        className="inline-flex min-h-11 items-center text-[14px] text-signal underline-offset-2 hover:underline"
      >
        Открыть «Ждёт меня» целиком
      </Link>
    </div>
  );
}

function InboxRow({ item: i, onNavigate }: { item: InboxItem; onNavigate: () => void }) {
  return (
    <li className="flex flex-col gap-2 px-3 py-3">
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
        <Badge tone="neutral">{INBOX_SOURCES[i.source]}</Badge>
        {i.projectCode ? <span>{i.projectCode}</span> : null}
        {i.due ? (
          <span className={clsx('num', i.overdue && 'font-medium text-danger')}>
            {i.overdue ? 'просрочено, ' : ''}до {date(i.due)}
          </span>
        ) : null}
      </div>
      <Link
        to={i.actionPath}
        onClick={onNavigate}
        className="inline-flex min-h-11 items-center text-[14px] font-medium [overflow-wrap:anywhere] text-ink hover:underline md:min-h-0"
      >
        {i.title}
      </Link>
      {i.details ? <p className="truncate text-[13px] text-ink-2">{i.details}</p> : null}
      <Link
        to={i.actionPath}
        onClick={onNavigate}
        className="inline-flex h-11 w-fit items-center rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8"
      >
        {i.actionLabel}
      </Link>
    </li>
  );
}

const tasksQuery = (scope: 'mine' | 'issued') => ({
  queryKey: ['tasks', scope] as const,
  queryFn: () => api<TaskView[]>('/tasks', { query: { scope, all: scope === 'issued' } }),
});
const peopleQuery = {
  queryKey: ['people'] as const,
  queryFn: () => api<Person[]>('/people'),
};

function HubTasks() {
  const qc = useQueryClient();
  const myId = useMe().user.id;
  const [scope, setScope] = useState<'mine' | 'issued'>('mine');
  const [creating, setCreating] = useState(false);
  const q = useQuery(tasksQuery(scope));
  const act = useMutation({
    mutationFn: (v: { id: string; action: 'done' | 'cancel' | 'reopen' }) =>
      api<TaskView>(`/tasks/${v.id}/${v.action}`, { body: {} }),
    onSuccess: (t) => {
      for (const k of ['tasks', 'inbox']) qc.invalidateQueries({ queryKey: [k] });
      toast(
        t.status === 'done'
          ? 'Поручение выполнено'
          : t.status === 'cancelled'
            ? 'Поручение отменено'
            : 'Поручение снова в работе',
      );
    },
  });
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-ink">Поручения</h3>
        <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
          Выдать
        </Button>
      </div>
      <div className="mb-3 inline-flex rounded-md border border-line bg-surface p-0.5">
        {(
          [
            ['mine', 'Мне'],
            ['issued', 'Выданные'],
          ] as const
        ).map(([k, t]) => (
          <button
            key={k}
            type="button"
            onClick={() => setScope(k)}
            className={clsx(
              'h-11 rounded-[3px] px-3 text-[13px] font-medium md:h-8',
              scope === k ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data!.length ? (
        <ul className="divide-y divide-line rounded-md border border-line">
          {q.data!.map((t) => (
            <li key={t.id} className="flex flex-col gap-2 px-3 py-3">
              <p
                className={clsx('text-[14px] font-medium', t.status !== 'open' && 'text-ink-2 line-through')}
              >
                {t.title}
              </p>
              <p className="text-[12px] text-ink-2">
                {scope === 'mine'
                  ? `${t.assigneeId !== myId ? `за ${t.assigneeName} · ` : ''}от ${t.createdByName ?? '—'}`
                  : `исполнитель ${t.assigneeName}`}
                {t.projectCode ? ` · ${t.projectCode}` : ''}
                {t.dueDate ? ` · до ${date(t.dueDate)}` : ''}
              </p>
              {t.linkPath ? (
                <Link
                  to={t.linkPath}
                  className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
                >
                  К записи обсуждения
                </Link>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {t.status === 'open' ? (
                  <Button
                    size="sm"
                    loading={act.isPending}
                    onClick={() => act.mutate({ id: t.id, action: 'done' })}
                  >
                    Выполнено
                  </Button>
                ) : (
                  <Badge tone={t.status === 'done' ? 'success' : 'neutral'}>
                    {t.status === 'done' ? 'выполнено' : 'отменено'}
                  </Badge>
                )}
                {scope === 'issued' && t.status === 'open' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => act.mutate({ id: t.id, action: 'cancel' })}
                  >
                    Отменить
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">
          {scope === 'mine' ? 'Поручений вам нет.' : 'Вы ещё не выдавали поручений.'}
        </p>
      )}
      <ErrorBox error={act.error} className="mt-2" />
      {creating ? <HubNewTask onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

function HubNewTask({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const people = useQuery(peopleQuery);
  const projects = useQuery(projectsQuery('active'));
  const [f, setF] = useState({ title: '', assigneeId: '', dueDate: '', projectId: '', details: '' });
  const m = useMutation({
    mutationFn: () =>
      api<TaskView>('/tasks', {
        body: {
          title: f.title.trim(),
          assigneeId: f.assigneeId,
          dueDate: f.dueDate || null,
          projectId: f.projectId || null,
          details: f.details.trim() || null,
        },
      }),
    onSuccess: (t) => {
      for (const k of ['tasks', 'inbox']) qc.invalidateQueries({ queryKey: [k] });
      toast(`Поручение выдано: ${t.assigneeName}`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новое поручение"
      description="Исполнитель увидит его в «Ждёт меня»."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={f.title.trim().length < 3 || !f.assigneeId}
            onClick={() => m.mutate()}
          >
            Выдать
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label="Что сделать">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </Field>
        <Field label="Исполнитель">
          <Select value={f.assigneeId} onChange={(e) => setF({ ...f, assigneeId: e.target.value })}>
            <option value="">— выберите —</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.position ? `, ${p.position}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок">
          <Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
        </Field>
        <Field label="Проект">
          <Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            <option value="">— без проекта —</option>
            {(projects.data ?? []).map((p: ProjectSummary) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Подробности">
          <Textarea rows={3} value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}
